import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import Modal from "react-modal";
import translit from "translitit-cyrillic-russian-to-latin";

import { getComments, sendComment } from "~/actions/comments";
import { eraseStateProp } from "~/actions/dashboard";
import { COMMENT_TYPE_OPTIONS } from "~/common/constants";
import { isPlanApprovalStatus } from "~/common/courses";
import { FormatDate } from "~/components/common/FormatDate";
import { formatPersonName } from "~/components/common/PersonName";
import { Img } from "~/components/ui/image";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { API_MEETINGS_IMAGE, getPlanLink } from "~/config";
import { useCmdEnterSubmit } from "~/hooks/use-cmd-enter-submit";
import type { TComment } from "~/reducers/login";
import { useAppDispatch, useAppSelector } from "~/store";
import type { TPatient } from "~/types/patient";
import type { TUser } from "~/types/user";

import PatientAskQuestion from "../patient_ask_question";

export function PatientComments({ patient, user }: { patient: TPatient; user: TUser }) {
  const dispatch = useAppDispatch();

  const intl = useIntl();
  const comments = useAppSelector((state) => state.comments);
  const token = useAppSelector((state) => state.token);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useCmdEnterSubmit(textareaRef, handleSubmit);

  useEffect(() => {
    Modal.setAppElement("body");
    void dispatch(getComments(patient.patient_id));

    return () => {
      dispatch(eraseStateProp("comments"));
    };
  }, [dispatch, patient.patient_id]);

  async function handleSubmit() {
    const textarea = textareaRef.current;

    if (!textarea || textarea.value.trim() == "") {
      setHasErrors(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(sendComment(patient.patient_id, textarea.value));
      textarea.value = "";
    } finally {
      setIsSubmitting(false);
      setHasErrors(false);
    }
  }

  const maxSkeletons = 3;
  const numberOfSkeletons = Math.min(patient.num_comments, maxSkeletons);

  return (
    <Portlet id="comment-block">
      <PortletTitle as="h2" iconClassName="icon-speech" id="comment-title">
        <FormattedMessage id="pat.comment.title" />
      </PortletTitle>

      <div className="portlet-body form">
        {isPlanApprovalStatus(patient.status) ? (
          <div className="alert alert-warning" id="comment-form-mobile">
            <span style={{ color: "black" }} id="comment-form-mobile-warning">
              <FormattedMessage id="comment.message.on.plan.approve" />
              {". "}
            </span>

            {token &&
            typeof patient.media["3d-plan"] == "object" &&
            patient.media["3d-plan"].response ? (
              <a
                href={getPlanLink(patient.patient_id, token, intl.locale)}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-underline"
              >
                <FormattedMessage id="pat.plan.button" />
              </a>
            ) : null}
          </div>
        ) : (
          <div className="form-body">
            <div className="form-group" id="comment-form-non-mobile">
              <label
                htmlFor="comment-form-non-mobile-input"
                className="control-label"
                id="comment-form-non-mobile-label"
              >
                <FormattedMessage id="pat.comment.button" />
              </label>
              <textarea
                data-matomo-mask
                className="form-control max-sm:tw-text-base"
                id="comment-form-non-mobile-input"
                rows={5}
                name="comment"
                defaultValue=""
                ref={textareaRef}
              />
              {hasErrors ? (
                <p className="tw-mb-0 tw-mt-[5px] tw-text-red-500">
                  <FormattedMessage id="pat.empty.comment" />
                </p>
              ) : null}
            </div>
          </div>
        )}

        <div className="form-actions">
          {isPlanApprovalStatus(patient.status) ? null : (
            <LoadingButton
              id="add-comment-btnnn comment-button-add-comment"
              type="submit"
              onClick={handleSubmit}
              style={{ margin: "10px" }}
              isLoading={isSubmitting}
            >
              <FormattedMessage id="pat.comment.button" />
            </LoadingButton>
          )}
          {isPlanApprovalStatus(patient.status) ? null : <PatientAskQuestion />}
        </div>
      </div>

      <div className="portlet-body">
        <div className="timeline">
          {Array.isArray(comments)
            ? comments.map((comment, i) => <Comment key={i} user={user} comment={comment} />)
            : Array.from({ length: numberOfSkeletons }, (_, index) => (
                <Comment key={index} user={user} comment={undefined} />
              ))}
        </div>
      </div>
    </Portlet>
  );
}

function Comment({ user, comment }: { user: TUser; comment: TComment | undefined }) {
  const intl = useIntl();

  const { color: hintColor = "", translation: hintIntlId = "" } = comment
    ? COMMENT_TYPE_OPTIONS[comment.type] ?? {}
    : {};

  const imageLoadingElement = <Skeleton width={75} height={75} className="tw-rounded-full" />;

  const employeeImageElement = (
    <div className="timeline-icon">
      <i className="icon-user font-purple" />
    </div>
  );

  return (
    <div className="timeline-item" id="comment-body">
      <div className="timeline-badge">
        {comment == undefined ? (
          imageLoadingElement
        ) : comment.commenter.avatar ? (
          <Img
            width={75}
            height={75}
            errorFallback={employeeImageElement}
            loadingFallback={imageLoadingElement}
            src={API_MEETINGS_IMAGE(comment.account_id, comment.commenter.avatar)}
            alt=""
          />
        ) : comment.account_id == user.account_id ? (
          <div className="timeline-icon">
            <i className="icon-user font-red-haze" />
          </div>
        ) : (
          employeeImageElement
        )}
      </div>

      <div className="timeline-body">
        {comment?.viewer_plan ? (
          <p>
            <FormattedMessage id="viewer.version.option" values={{ correction: comment.viewer_plan.correction_index, version: comment.viewer_plan.version }} />
          </p>
        ) : null}
        <div className="timeline-body-arrow"></div>

        <div className="timeline-body-head">
          <div className="timeline-body-head-caption">
            <span
              data-matomo-mask
              data-hj-suppress
              className="timeline-body-title font-blue-madison"
              id="comment-block-authon"
            >
              {comment == undefined ? null : comment.account_id == user.account_id ? (
                <TranslitFromCyrillic
                  id={formatPersonName({
                    person: { ...comment.commenter, account_id: comment.account_id },
                    user,
                  })}
                  locale={intl.locale}
                />
              ) : comment.commenter.external ? (
                comment.commenter.full_name
              ) : (
                <TranslitFromCyrillic
                  id={formatPersonName({
                    person: { ...comment.commenter, account_id: comment.account_id },
                    user,
                  })}
                  locale={intl.locale}
                />
              )}
            </span>
            &nbsp;
            {comment ? (
              <FormatDate
                className="timeline-body-time font-grey-cascade"
                value={comment.timestamp}
                date
              />
            ) : null}
          </div>

          <div className="timeline-body-head-actions">
            {hintColor && hintIntlId ? (
              <span style={{ color: hintColor, fontWeight: "600", fontSize: "12px" }}>
                <FormattedMessage id={hintIntlId} />
              </span>
            ) : null}
          </div>
        </div>

        <div className="timeline-body-content">
          <span
            data-matomo-mask
            data-hj-suppress
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            className="font-grey-cascade"
            id="comment-block-content"
          >
            {comment ? comment.comment : <Skeleton containerClassName="tw-space-y-1" count={2} />}
          </span>
        </div>
      </div>
    </div>
  );
}

function TranslitFromCyrillic({ id, locale }: { id: string; locale: string }) {
  if (locale !== "ru") {
    return <span>{translit(id)}</span>;
  } else {
    return <span>{id}</span>;
  }
}
